import * as React from "react";
import { cn } from "../lib/cn.js";
import { Button } from "./button.js";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select.js";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table.js";
import {
  DataTableCardList,
  tableColumnCount,
  useBelowSm,
  type DataTableResponsive,
} from "./data-table-cards.js";

const DataTableResponsiveContext = React.createContext<DataTableResponsive>("auto");

/**
 * DataTable — opinionated wrapper enforcing Iron Rule #16.
 *
 * Composition:
 *   <DataTable>
 *     <DataTableToolbar> filter input, chips, columns, density </DataTableToolbar>
 *     <DataTableScroll>
 *       <Table>
 *         <TableHeader>…</TableHeader>
 *         <TableBody>…</TableBody>
 *       </Table>
 *     </DataTableScroll>
 *     <DataTableFooter>
 *       <DataTableSelectionSummary selected=0 total=10 />
 *       <DataTablePagination … />
 *     </DataTableFooter>
 *   </DataTable>
 *
 * Why composable instead of a config-driven mega-component? Because
 * different pages need different filter chips, different action
 * columns, different empty states. A composable shell stays out of
 * the way while still enforcing the SAME chrome (sticky head, scroll
 * region, footer outside scroll).
 *
 * See `.windsurf/rules/iron-rules.md` §16 for the full spec.
 */

interface DataTableProps extends React.ComponentPropsWithoutRef<"div"> {
  /**
   * Below `sm`, a table with more than 4 columns renders each row as a
   * card (Iron Rule #16). `auto` (the default) does that. `cards` uses
   * cards below `sm` whatever the column count. `table` keeps the grid,
   * for registers that scroll sideways instead.
   */
  responsive?: DataTableResponsive;
}

const DataTable = React.forwardRef<HTMLDivElement, DataTableProps>(
  ({ className, responsive = "auto", ...props }, ref) => (
    <DataTableResponsiveContext.Provider value={responsive}>
      <div
        ref={ref}
        data-slot="data-table"
        className={cn(
          // The DataTable owns a vertical flex container so the toolbar
          // and footer stay outside the scroll region while the body
          // (the only flex-1 child) handles overflow internally.
          // `min-h-0` lets the table participate in a flex chain when
          // a parent gives it `flex-1` (so DataTableScroll can fill the
          // remaining viewport).
          "border-border/60 bg-card flex min-h-0 flex-col overflow-hidden rounded-lg border",
          className,
        )}
        {...props}
      />
    </DataTableResponsiveContext.Provider>
  ),
);
DataTable.displayName = "DataTable";

const DataTableToolbar = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="data-table-toolbar"
      className={cn(
        // Standard slots in canonical order (Iron Rule #16.2):
        //   filter input | chips | (spacer) | columns | density
        // Consumer is responsible for slot order; we just provide the
        // flex container + spacing.
        "border-border/60 flex flex-wrap items-center gap-2 border-b px-4 py-3",
        className,
      )}
      {...props}
    />
  ),
);
DataTableToolbar.displayName = "DataTableToolbar";

const DataTableToolbarSpacer = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div ref={ref} aria-hidden="true" className={cn("flex-1", className)} {...props} />
));
DataTableToolbarSpacer.displayName = "DataTableToolbarSpacer";

/**
 * The vertical scroll container. The `<thead>` inside `<Table>` is
 * `position: sticky; top: 0` — relative to THIS container — so the
 * column headings stay visible while only the row body scrolls.
 *
 * `maxHeight` defaults to `calc(100dvh - 240px)` which leaves room
 * for: Topbar (64px) + main py-8 (64px combined) + PageHeader
 * (~96px) + DataTable toolbar/footer chrome (~64px). On a typical
 * 1080p monitor this resolves to ~840px — enough for ~14 rows of
 * comfortable density before inner scroll kicks in. On a 13"
 * laptop (~768px viewport) it resolves to ~528px, ~9 rows.
 *
 * `100dvh` (dynamic viewport units) excludes mobile browser UI so
 * iOS Safari's collapsing toolbar doesn't desync the table size.
 *
 * Override per-page for embedded panels (e.g. "last 10 events" on a
 * dashboard tile: `<DataTableScroll maxHeight="320px" />`).
 */
interface DataTableScrollProps extends React.ComponentPropsWithoutRef<"div"> {
  /**
   * CSS max-height for the scroll region. Accepts any Tailwind /
   * inline value. Default fills the available viewport so short
   * tables don't force inner scroll, while long tables get a
   * sticky-thead scroll region that fits the screen.
   */
  maxHeight?: string;
}

const DataTableScroll = React.forwardRef<HTMLDivElement, DataTableScrollProps>(
  ({ className, style, maxHeight, children, ...props }, ref) => {
    const responsive = React.useContext(DataTableResponsiveContext);
    const belowSm = useBelowSm();
    const columns = tableColumnCount(children);
    const wantsCards =
      belowSm && (responsive === "cards" || (responsive === "auto" && columns > 4));
    const cards = wantsCards ? <DataTableCardList>{children}</DataTableCardList> : null;

    return (
      <div
        ref={ref}
        data-slot="data-table-scroll"
        className={cn(
          // The `relative` is needed so the sticky thead has a proper
          // containing block. `overflow-auto` (not just `overflow-y`)
          // lets horizontal-overflow scenarios trigger sticky-first-
          // column on consumers that opt in.
          "relative overflow-auto",
          // When no explicit max-height is provided, fill the parent
          // flex column. Combined with the parent <DataTable> being
          // `flex flex-col` and the page wrapping the table in a
          // `flex-1 min-h-0` flex chain, the scroll region grows to
          // exactly the available viewport space — no calc()-guesses,
          // no over-tall page scroll, no inner scroll on short data.
          maxHeight ? null : "min-h-0 flex-1",
          className,
        )}
        style={maxHeight ? { maxHeight, ...style } : style}
        {...props}
      >
        {cards ?? children}
      </div>
    );
  },
);
DataTableScroll.displayName = "DataTableScroll";

const DataTableFooter = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="data-table-footer"
      className={cn(
        // The footer sits OUTSIDE the scroll container so the row body
        // can scroll independently while the selection / pagination
        // controls stay anchored.
        "border-border/60 text-muted-foreground flex flex-wrap items-center justify-between gap-3 border-t px-4 py-3 text-sm",
        className,
      )}
      {...props}
    />
  ),
);
DataTableFooter.displayName = "DataTableFooter";

interface DataTableSelectionSummaryProps extends React.HTMLAttributes<HTMLDivElement> {
  selected: number;
  total: number;
}

/**
 * "<n> of <total> row(s) selected." per Iron Rule #16.1. Always
 * rendered, even when n=0, so the footer height never jitters on
 * first selection.
 */
const DataTableSelectionSummary = React.forwardRef<HTMLDivElement, DataTableSelectionSummaryProps>(
  ({ className, selected, total, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="data-table-selection-summary"
      className={cn("text-muted-foreground text-sm", className)}
      aria-live="polite"
      {...props}
    >
      <span className="tabular-nums">{selected}</span> of{" "}
      <span className="tabular-nums">{total}</span> row(s) selected.
    </div>
  ),
);
DataTableSelectionSummary.displayName = "DataTableSelectionSummary";

// `title` on HTMLAttributes is a `string` (the native tooltip
// attribute), but the empty-state title is a ReactNode. Omit the
// native one so we can widen it without a type clash.
interface DataTableEmptyProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Slot for a primary CTA (a `<Button>`). */
  action?: React.ReactNode;
}

const DataTableEmpty = React.forwardRef<HTMLDivElement, DataTableEmptyProps>(
  ({ className, icon, title, description, action, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="data-table-empty"
      className={cn(
        "flex min-h-[240px] flex-col items-center justify-center gap-3 px-6 py-12 text-center",
        className,
      )}
      {...props}
    >
      {icon ? <div className="text-muted-foreground">{icon}</div> : null}
      <div className="space-y-1">
        <p className="text-foreground text-sm font-medium">{title}</p>
        {description ? <p className="text-muted-foreground text-sm">{description}</p> : null}
      </div>
      {action}
    </div>
  ),
);
DataTableEmpty.displayName = "DataTableEmpty";

interface DataTableSkeletonProps {
  /** Number of skeleton rows. Iron Rule #16.4 mandates ≥ 5. */
  rows?: number;
  /** Number of skeleton columns. */
  columns?: number;
  className?: string;
}

/**
 * Skeleton placeholder for table loading state. Renders inside a
 * `<TableBody>` so consumers can drop it directly between
 * `<DataTableScroll>` and the real data without restructuring.
 *
 * Animation: a 1200ms linear `translateX` shimmer (Iron Rule #16.5).
 * GPU-only properties — no width/height transitions, no opacity
 * pulse that compounds on long lists.
 */
const DataTableSkeleton = React.forwardRef<HTMLTableSectionElement, DataTableSkeletonProps>(
  ({ rows = 6, columns = 4, className }, ref) => (
    <tbody
      ref={ref}
      data-slot="data-table-skeleton"
      className={cn(
        "[&>tr]:border-border/60 [&>tr:last-child]:border-b-0 [&>tr]:border-b",
        className,
      )}
    >
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <tr key={rowIdx}>
          {Array.from({ length: columns }).map((_, colIdx) => (
            <td key={colIdx} className="px-4 py-4 align-middle">
              <span
                aria-hidden="true"
                className={cn(
                  "bg-muted/60 relative block h-4 w-full overflow-hidden rounded",
                  "before:absolute before:inset-0 before:-translate-x-full before:bg-gradient-to-r before:from-transparent before:via-white/40 before:to-transparent before:content-['']",
                  "before:[animation-duration:1200ms] before:[animation-iteration-count:infinite] before:[animation-name:data-table-shimmer] before:[animation-timing-function:linear]",
                  "motion-reduce:before:animate-none",
                )}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  ),
);
DataTableSkeleton.displayName = "DataTableSkeleton";

/**
 * Canonical rows-per-page options (Iron Rule #16.1). The default is
 * 50 to match the historical hardcoded value used across the People +
 * Admissions tables (matching what users are already accustomed to);
 * the spec mandates 10/25/50/100 as the available choices and 10 as
 * the canonical default for NEW tables. Consumers may override
 * `pageSizeOptions` for specialised cases (e.g. a "Recent activity"
 * widget that only makes sense at 10/25).
 */
const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

export type PageSizeOption = (typeof DEFAULT_PAGE_SIZE_OPTIONS)[number] | number;

interface DataTablePaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Current 1-indexed page number. */
  page: number;
  /** Current page size (rows-per-page). */
  pageSize: number;
  /** Total row count (across all pages). */
  totalRows: number;
  /** Available rows-per-page choices. Defaults to [10, 25, 50, 100]. */
  pageSizeOptions?: readonly number[];
  /**
   * Called when the user picks a new page. The handler is responsible
   * for clamping (the primitive guards prev/next buttons but a
   * consumer that maps page-state into a URL must clamp on its own
   * boundary checks too).
   */
  onPageChange: (nextPage: number) => void;
  /**
   * Called when the user picks a new rows-per-page. The handler
   * should typically also reset `page` to 1 so the user lands on a
   * coherent first page after a size change — the primitive does NOT
   * do this implicitly because some consumers (e.g. URL-state pages)
   * want to keep the offset stable instead.
   */
  onPageSizeChange: (nextPageSize: number) => void;
  /**
   * When true, disable the page-nav buttons + size selector. Use
   * during in-flight refetches so the user can't queue a second nav
   * before the first response lands.
   */
  isFetching?: boolean;
  /** Optional label for the rows-per-page selector. Defaults to "Rows per page". */
  pageSizeLabel?: string;
  /** Optional data-testid prefix so consumers can scope queries. */
  testIdPrefix?: string;
}

/**
 * Iron Rule #16 mandated pagination primitive.
 *
 * Renders, left → right:
 *   - "Rows per page" Select with options [10, 25, 50, 100]
 *   - "Page m of p" indicator (tabular-nums so digits don't jitter)
 *   - Four navigation buttons with the spec-mandated glyphs:
 *       «  first page
 *       ‹  previous page
 *       ›  next page
 *       »  last page
 *
 * Prev/next/first/last buttons are disabled when at the corresponding
 * boundary OR when `isFetching` is true. The rows-per-page selector
 * is disabled only when `isFetching` is true — there's no harm in the
 * UI showing 50 when the current request was for 25.
 *
 * Use ALONGSIDE `<DataTableFooter>` (not inside it directly — the
 * footer is a flex row with a selection-summary slot on the left, so
 * this primitive is the typical right-hand child). See StudentsTab
 * for the canonical wiring.
 */
const DataTablePagination = React.forwardRef<HTMLDivElement, DataTablePaginationProps>(
  (
    {
      className,
      page,
      pageSize,
      totalRows,
      pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
      onPageChange,
      onPageSizeChange,
      isFetching = false,
      pageSizeLabel = "Rows per page",
      testIdPrefix,
      ...props
    },
    ref,
  ) => {
    const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    const atFirst = page <= 1;
    const atLast = page >= totalPages;
    const handleFirst = React.useCallback(() => onPageChange(1), [onPageChange]);
    const handlePrev = React.useCallback(
      () => onPageChange(Math.max(1, page - 1)),
      [onPageChange, page],
    );
    const handleNext = React.useCallback(
      () => onPageChange(Math.min(totalPages, page + 1)),
      [onPageChange, page, totalPages],
    );
    const handleLast = React.useCallback(
      () => onPageChange(totalPages),
      [onPageChange, totalPages],
    );
    const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

    return (
      <div
        ref={ref}
        data-slot="data-table-pagination"
        className={cn("flex flex-wrap items-center gap-3", className)}
        {...props}
      >
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-xs">{pageSizeLabel}</span>
          <Select
            value={String(pageSize)}
            onValueChange={(nextValue) => {
              const parsed = Number(nextValue);
              if (Number.isFinite(parsed) && parsed > 0) onPageSizeChange(parsed);
            }}
            disabled={isFetching}
          >
            <SelectTrigger
              className="h-8 w-[72px]"
              aria-label={pageSizeLabel}
              data-testid={testId("page-size")}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span
          className="text-muted-foreground text-xs tabular-nums"
          data-testid={testId("page-indicator")}
        >
          Page <span className="text-foreground">{page.toLocaleString()}</span> of{" "}
          <span className="text-foreground">{totalPages.toLocaleString()}</span>
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 font-mono text-base leading-none"
            onClick={handleFirst}
            disabled={atFirst || isFetching}
            aria-label="First page"
            data-testid={testId("first")}
          >
            <span aria-hidden="true">«</span>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 font-mono text-base leading-none"
            onClick={handlePrev}
            disabled={atFirst || isFetching}
            aria-label="Previous page"
            data-testid={testId("previous")}
          >
            <span aria-hidden="true">‹</span>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 font-mono text-base leading-none"
            onClick={handleNext}
            disabled={atLast || isFetching}
            aria-label="Next page"
            data-testid={testId("next")}
          >
            <span aria-hidden="true">›</span>
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 font-mono text-base leading-none"
            onClick={handleLast}
            disabled={atLast || isFetching}
            aria-label="Last page"
            data-testid={testId("last")}
          >
            <span aria-hidden="true">»</span>
          </Button>
        </div>
      </div>
    );
  },
);
DataTablePagination.displayName = "DataTablePagination";

export {
  DataTable,
  DataTablePagination,
  DataTableToolbar,
  DataTableToolbarSpacer,
  DataTableScroll,
  DataTableFooter,
  DataTableSelectionSummary,
  DataTableEmpty,
  DataTableSkeleton,
  type DataTableResponsive,
  // Re-export the lower-level primitives so consumers don't have to
  // double-import from "./table.js" + "./data-table.js".
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
